/**
 * Just enough of the zip format to read a .pptx, .docx or .odt.
 *
 * Those formats are zip archives of XML, so getting the words out of a set of
 * slides means opening one. The browser can already inflate — DecompressionStream
 * has been in every engine this app runs on for years — so this is the index
 * around it and nothing more: find the central directory, find the entries
 * asked for, inflate those.
 *
 * Deliberately narrow. It reads, never writes; it decompresses only the
 * entries named, so the images in a deck are skipped; it refuses anything
 * oversized before allocating; and it knows exactly two compression methods.
 * Anything stranger is an error with a sentence, not a guess.
 */

export class ZipError extends Error {}

const EOCD_SIGNATURE = 0x06054b50;
const CENTRAL_SIGNATURE = 0x02014b50;
const LOCAL_SIGNATURE = 0x04034b50;

/** A zip comment can be 64 kB, so the end record is somewhere in the last of it. */
const EOCD_SEARCH_BYTES = 65_557;

/** One entry's uncompressed size, past which this is not a document. */
const MAX_ENTRY_BYTES = 64 * 1024 * 1024;

export interface ZipEntryInfo {
  name: string;
  compressedSize: number;
  uncompressedSize: number;
  /** 0 stored, 8 deflate. Anything else this cannot read. */
  method: number;
  localHeaderOffset: number;
}

function findEndOfCentralDirectory(view: DataView): number {
  const start = Math.max(0, view.byteLength - EOCD_SEARCH_BYTES);
  for (let i = view.byteLength - 22; i >= start; i -= 1) {
    if (view.getUint32(i, true) === EOCD_SIGNATURE) return i;
  }
  throw new ZipError('That file is not a zip archive, so its contents cannot be read.');
}

/** Every entry in the archive, by name, without decompressing anything. */
export function listZipEntries(data: Uint8Array): Map<string, ZipEntryInfo> {
  if (data.byteLength < 22) throw new ZipError('That file is too small to be a zip archive.');
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);

  const eocd = findEndOfCentralDirectory(view);
  const count = view.getUint16(eocd + 10, true);
  let offset = view.getUint32(eocd + 16, true);

  if (offset === 0xffffffff) {
    throw new ZipError('That archive uses the zip64 format, which this cannot read.');
  }

  const entries = new Map<string, ZipEntryInfo>();
  const names = new TextDecoder();

  for (let i = 0; i < count; i += 1) {
    if (offset + 46 > data.byteLength || view.getUint32(offset, true) !== CENTRAL_SIGNATURE) {
      throw new ZipError('That archive’s index is damaged.');
    }
    const method = view.getUint16(offset + 10, true);
    const compressedSize = view.getUint32(offset + 20, true);
    const uncompressedSize = view.getUint32(offset + 24, true);
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const localHeaderOffset = view.getUint32(offset + 42, true);

    const name = names.decode(data.subarray(offset + 46, offset + 46 + nameLength));
    entries.set(name, { name, compressedSize, uncompressedSize, method, localHeaderOffset });

    offset += 46 + nameLength + extraLength + commentLength;
  }

  return entries;
}

async function inflate(bytes: Uint8Array): Promise<Uint8Array> {
  if (typeof DecompressionStream === 'undefined') {
    throw new ZipError('This browser cannot decompress zip archives.');
  }
  // "deflate-raw": a zip entry holds a bare deflate stream, with none of the
  // zlib header that plain "deflate" expects.
  //
  // Fed from a stream built here rather than from a Blob: it saves a copy of
  // every entry, and Blob.stream() is one of the things a test environment is
  // most likely not to have.
  const source = new ReadableStream<BufferSource>({
    start(controller) {
      // Copied so the chunk owns a plain ArrayBuffer, which is what a
      // byte stream is typed to take.
      controller.enqueue(new Uint8Array(bytes));
      controller.close();
    },
  });
  const inflated = source.pipeThrough(new DecompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(inflated).arrayBuffer());
}

/** One entry's bytes, inflated if it needs it. */
export async function readZipEntry(data: Uint8Array, entry: ZipEntryInfo): Promise<Uint8Array> {
  if (entry.uncompressedSize > MAX_ENTRY_BYTES) {
    throw new ZipError(`${entry.name} is too large to open here.`);
  }

  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const header = entry.localHeaderOffset;
  if (header + 30 > data.byteLength || view.getUint32(header, true) !== LOCAL_SIGNATURE) {
    throw new ZipError(`${entry.name} is not where the archive’s index says it is.`);
  }

  const nameLength = view.getUint16(header + 26, true);
  const extraLength = view.getUint16(header + 28, true);
  const start = header + 30 + nameLength + extraLength;
  const body = data.subarray(start, start + entry.compressedSize);

  if (entry.method === 0) return body;
  if (entry.method === 8) return inflate(body);
  throw new ZipError(`${entry.name} is compressed in a way this cannot read.`);
}

/** The named entries, inflated and decoded as UTF-8 text. */
export async function readZipText(
  data: Uint8Array,
  wanted: (name: string) => boolean,
): Promise<Map<string, string>> {
  const entries = listZipEntries(data);
  const decoder = new TextDecoder();
  const out = new Map<string, string>();
  for (const entry of entries.values()) {
    if (!wanted(entry.name)) continue;
    out.set(entry.name, decoder.decode(await readZipEntry(data, entry)));
  }
  return out;
}
